import { audioEngine } from './audioEngine';
import { AdMob, RewardAdOptions, AdMobRewardItem, AdMobError, RewardAdPluginEvents } from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';

/**
 * Universal Ad Manager for numQ
 * Handles native AdMob integration with a graceful mock fallback for Web.
 * 
 * APP ID: ca-app-pub-5386067497232421~9471676854
 */

// TOGGLE THIS FOR PRODUCTION
const USE_PRODUCTION_ADS = true;

export type AdRewardType = 'heart_refill' | 'daily_double' | 'ad_box' | 'star_practice';

class AdManager {
  private isInitialized = false;
  private isMobile = Capacitor.getPlatform() !== 'web';

  // Ad Unit IDs mapping
  private readonly TEST_UNITS: Record<AdRewardType, string> = {
    heart_refill: 'ca-app-pub-3940256099942544/5224354917',
    daily_double: 'ca-app-pub-3940256099942544/5224354917',
    ad_box: 'ca-app-pub-3940256099942544/5224354917',
    star_practice: 'ca-app-pub-3940256099942544/5224354917',
  };

  private readonly PROD_UNITS: Record<AdRewardType, string> = {
    daily_double: 'ca-app-pub-5386067497232421/1803969624',
    heart_refill: 'ca-app-pub-5386067497232421/1270828607',
    ad_box: 'ca-app-pub-5386067497232421/2247051664',
    star_practice: 'ca-app-pub-5386067497232421/3855646806',
  };

  /**
   * Initialize AdMob on app startup
   */
  async initialize() {
    if (this.isInitialized || !this.isMobile) return;

    try {
      await AdMob.initialize({
        // Set to true only when testing — false = serve real production ads
        initializeForTesting: !USE_PRODUCTION_ADS,
      });

      // Request user tracking permission (required on iOS)
      try {
        const trackingInfo = await AdMob.trackingAuthorizationStatus();
        if (trackingInfo.status === 'notDetermined') {
          await AdMob.requestTrackingAuthorization();
        }
      } catch (trackError) {
        console.warn('AdMob tracking authorization status request failed:', trackError);
      }

      this.isInitialized = true;
      console.log('AdMob Initialized (Prod Mode:', USE_PRODUCTION_ADS, ')');
    } catch (e) {
      console.error('AdMob initialization failed:', e);
    }
  }

  /**
   * Shows a rewarded ad and returns true if the user completed it
   */
  async showRewardedAd(type: AdRewardType): Promise<boolean> {
    if (!this.isMobile) {
      return this.showMockAd(type);
    }

    try {
      const adId = USE_PRODUCTION_ADS ? this.PROD_UNITS[type] : this.TEST_UNITS[type];
      
      // 1. Prepare/Load the ad using the specific ID for this type
      const options: RewardAdOptions = {
        adId: adId,
        // isTesting: !USE_PRODUCTION_ADS,
      };

      await AdMob.prepareRewardVideoAd(options);

      // 2. Show the ad
      return new Promise(async (resolve) => {
        let rewardReceived = false;
        const listeners: any[] = [];

        const cleanup = async () => {
          for (const handle of listeners) {
            await handle.remove();
          }
        };

        const onReward = (reward: AdMobRewardItem) => {
          console.log(`User earned reward (${type}):`, reward);
          rewardReceived = true;
        };

        const onDismiss = async () => {
          await cleanup();

          audioEngine.resumeAudio();
          resolve(rewardReceived);
        };

        const onError = async (error: AdMobError) => {
          console.error(`Ad error (${type}):`, error);
          await cleanup();

          audioEngine.resumeAudio();
          resolve(false);
        };

        // Add listeners
        listeners.push(await AdMob.addListener(RewardAdPluginEvents.Rewarded, onReward));
        listeners.push(await AdMob.addListener(RewardAdPluginEvents.Dismissed, onDismiss));
        listeners.push(await AdMob.addListener(RewardAdPluginEvents.FailedToLoad, onError));

        audioEngine.suspendAudio();
        await AdMob.showRewardVideoAd();
      });
    } catch (e) {
      console.error(`Failed to play rewarded ad (${type}):`, e);
      return false;
    }
  }

  /**
   * Simulated ad for web development
   */
  private async showMockAd(type: AdRewardType): Promise<boolean> {
    console.log(`Simulating Rewarded Ad for ${type} (Web Environment)...`);
    return new Promise((resolve) => {
      // Simulate a 2-second ad duration
      setTimeout(() => {
        resolve(true); // Always reward in dev/mock
      }, 2000);
    });
  }
}

export const adManager = new AdManager();
